import { type ErrorComponentProps, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { t } from "../i18n/en";
import { isStaleModuleLoadError } from "../pwa";
import { AppHeader } from "./AppHeader";

const MISSING_CHART = "Your primary chart is not available yet.";
const SIGN_IN = "Sign in to view your chart.";

export function AppNoticePage({
  title,
  message,
  children,
}: {
  title: string;
  message: string;
  children?: ReactNode;
}) {
  return (
    <main className="relative mx-auto w-full max-w-3xl px-[clamp(1.25rem,4vw,3.75rem)] py-6 sm:py-8">
      <AppHeader />
      <section className="mt-6 rounded-[2rem] border-2 border-black bg-white p-6 shadow-[8px_8px_0_#95A789] sm:p-8">
        <p className="font-mono-display text-xs uppercase tracking-[0.08em] text-[#6f8065]">
          Harness
        </p>
        <h1 className="harness-page-title mt-2 font-display font-bold lowercase">{title}</h1>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-black/65">{message}</p>
        {children ? <div className="mt-7 flex flex-wrap gap-3">{children}</div> : null}
      </section>
    </main>
  );
}

export function RouteErrorPage({ error, reset }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  const staleModuleError = isStaleModuleLoadError(message);

  if (message === SIGN_IN) {
    return (
      <AppNoticePage title={t("ui.copy_sign_in_to_see_your_chart_e64cf607")} message={message}>
        <NoticeLink to="/sign-in">{t("common.signIn")}</NoticeLink>
      </AppNoticePage>
    );
  }

  if (message === MISSING_CHART) {
    return (
      <AppNoticePage
        title={t("ui.copy_your_chart_is_not_ready_yet_15386f87")}
        message="Your primary chart is not available yet. Start your own reading, or open charts that were shared with you."
      >
        <NoticeLink to="/wizard">{t("common.doMyReading")}</NoticeLink>
        <NoticeLink to="/friends" tone="quiet">
          Friends
        </NoticeLink>
      </AppNoticePage>
    );
  }

  return (
    <AppNoticePage
      title={staleModuleError ? "refresh Harness" : "something went wrong"}
      message={
        staleModuleError
          ? "This page could not load. Choose Home to reload Harness and try again."
          : message
      }
    >
      {reset ? (
        <button
          type="button"
          onClick={() => reset()}
          className="inline-flex min-h-12 items-center rounded-full bg-[#95A789] px-6 py-3 text-sm uppercase tracking-[0.12em]"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          {t("ui.copy_try_again_df3a2661")}
        </button>
      ) : null}
      <a
        href="/"
        className="inline-flex min-h-12 items-center rounded-full border-2 border-black px-6 py-3 text-sm uppercase tracking-[0.12em]"
        style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
      >
        Home
      </a>
    </AppNoticePage>
  );
}

function NoticeLink({
  to,
  tone = "solid",
  children,
}: {
  to: "/" | "/sign-in" | "/wizard" | "/friends";
  tone?: "solid" | "quiet";
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={
        tone === "solid"
          ? "inline-flex min-h-12 items-center rounded-full bg-[#95A789] px-6 py-3 text-sm uppercase tracking-[0.12em]"
          : "inline-flex min-h-12 items-center rounded-full border-2 border-black px-6 py-3 text-sm uppercase tracking-[0.12em]"
      }
      style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
    >
      {children}
    </Link>
  );
}
